import type { ReactNode } from 'react';
import { numberOf } from './format';

export interface FieldProps {
  label: string;
  note?: string;
  children: ReactNode;
  /** A group of radios cannot sit inside one `<label>`; render a `<div>` instead. */
  block?: boolean;
}

export const Field = ({ label, note, children, block = false }: FieldProps) => {
  const heading = (
    <span className="lbl">
      {label}
      {note && <small>{note}</small>}
    </span>
  );

  return block ? (
    <div className="field">
      {heading}
      {children}
    </div>
  ) : (
    <label className="field">
      {heading}
      {children}
    </label>
  );
};

export interface Option {
  value: string;
  label: string;
}

export interface SelectFieldProps {
  label: string;
  note?: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const SelectField = ({ label, note, value, options, onChange, disabled = false }: SelectFieldProps) => (
  <Field
    label={label}
    note={note}
  >
    <select
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <option
          key={option.value}
          value={option.value}
        >
          {option.label}
        </option>
      ))}
    </select>
  </Field>
);

export interface SegProps {
  name: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
}

/** The black-on-white segmented switch of the HTML version: radios styled as adjoining buttons. */
export const Seg = ({ name, value, options, onChange }: SegProps) => (
  <div className="seg">
    {options.map((option) => (
      <label key={option.value}>
        <input
          type="radio"
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={() => onChange(option.value)}
        />
        <span>{option.label}</span>
      </label>
    ))}
  </div>
);

export interface NumberFieldProps {
  label: string;
  note?: string;
  value: number;
  /** Changing it remounts the input, which is how a quick-fill writes a new figure into an uncontrolled field. */
  fillKey: string | number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number) => void;
}

/**
 * Uncontrolled on purpose: a controlled number input snaps "2" back to "20" while someone is still
 * typing "25". The parsed value reaches the state on every keystroke all the same.
 */
export const NumberField = ({ label, note, value, fillKey, min, max, step, onChange }: NumberFieldProps) => (
  <Field
    label={label}
    note={note}
  >
    <input
      key={fillKey}
      type="number"
      defaultValue={value}
      min={min}
      max={max}
      step={step}
      onChange={(event) => onChange(numberOf(event.target.value))}
    />
  </Field>
);
